import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Users, Copy, Check, MessageCircle, Mail, Gift, Sparkles, TrendingUp } from 'lucide-react';
import { apiFetch } from '../services/api';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 1-Klick-Referral-Card fürs Chef-Dashboard.
 * WhatsApp / E-Mail / Copy-Link — Share-Texts vom Backend vorgeneriert.
 * Zeigt außerdem: geworbene Firmen + wartende Freimonate.
 */
export const ReferralShareCard = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [codeRes, statsRes] = await Promise.all([
          apiFetch(`${API_URL}/api/referral/my-code`).then(r => r.ok ? r.json() : null),
          apiFetch(`${API_URL}/api/referral/my-stats`).then(r => r.ok ? r.json() : null),
        ]);
        setData(codeRes);
        setStats(statsRes);
      } catch (e) {
        console.error('Referral-Load fehlgeschlagen:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data?.success) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_url);
      setCopied(true);
      toast({ title: 'Link kopiert!', description: 'Jetzt einfach an einen Kollegen schicken.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Fehler', description: 'Konnte Link nicht kopieren.', variant: 'destructive' });
    }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(data.share_texts.whatsapp);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(data.share_texts.email_subject);
    const body = encodeURIComponent(data.share_texts.email_body);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SmartVolt Academy',
          text: data.share_texts.whatsapp,
          url: data.referral_url,
        });
      } catch { /* User cancelled */ }
    } else {
      handleCopy();
    }
  };

  const qualified = stats?.qualified ?? 0;
  const pending = stats?.bonus_months_pending ?? 0;
  const total = stats?.total ?? 0;

  return (
    <Card
      className="relative overflow-hidden border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-500/[0.12] via-gray-900/60 to-amber-500/[0.08] p-6 shadow-2xl shadow-yellow-500/10"
      data-testid="referral-share-card"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-yellow-500/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center shrink-0">
            <Gift className="w-6 h-6 text-yellow-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-black text-lg">Kollegen einladen — 1 Monat gratis</h3>
              <span className="inline-flex items-center gap-1 bg-green-500/15 text-green-300 border border-green-500/40 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                <Sparkles className="w-3 h-3" /> Neu
              </span>
            </div>
            <p className="text-gray-400 text-sm mt-1">
              Für <strong className="text-yellow-300">jede Firma</strong>, die über deinen Link kauft, bekommst du <strong className="text-yellow-300">einen Monat geschenkt</strong>.
            </p>
          </div>
        </div>

        {/* Statistik-Zeile */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="rounded-lg bg-black/40 border border-gray-800 px-3 py-2" data-testid="referral-stat-total">
            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Empfehlungen</p>
            <p className="text-white font-black text-xl">{total}</p>
          </div>
          <div className="rounded-lg bg-black/40 border border-green-500/30 px-3 py-2" data-testid="referral-stat-qualified">
            <p className="text-[10px] text-green-400 uppercase font-bold tracking-wider">Gekauft</p>
            <p className="text-green-300 font-black text-xl">{qualified}</p>
          </div>
          <div className="rounded-lg bg-black/40 border border-yellow-500/40 px-3 py-2" data-testid="referral-stat-pending">
            <p className="text-[10px] text-yellow-400 uppercase font-bold tracking-wider">Freimonate</p>
            <p className="text-yellow-300 font-black text-xl">{pending}</p>
          </div>
        </div>

        {pending > 0 && (
          <div className="mb-4 rounded-lg bg-green-500/10 border border-green-500/40 px-4 py-2 flex items-start gap-2" data-testid="referral-pending-hint">
            <TrendingUp className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
            <p className="text-green-200 text-xs">
              <strong>{pending} Freimonat{pending === 1 ? '' : 'e'}</strong> für dich vorgemerkt — wird bei deiner nächsten Rechnung von Martin manuell gutgeschrieben.
            </p>
          </div>
        )}

        {/* Share-Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <Button
            onClick={handleWhatsApp}
            className="bg-[#25D366] hover:bg-[#20b657] text-white font-bold w-full"
            data-testid="referral-share-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
          </Button>
          <Button
            onClick={handleEmail}
            variant="outline"
            className="border-gray-700 text-gray-200 hover:bg-gray-800 font-bold w-full"
            data-testid="referral-share-email"
          >
            <Mail className="w-4 h-4 mr-2" /> E-Mail
          </Button>
          <Button
            onClick={handleCopy}
            className={`font-bold w-full ${copied ? 'bg-green-500 text-white' : 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400'}`}
            data-testid="referral-share-copy"
          >
            {copied ? <><Check className="w-4 h-4 mr-2" /> Kopiert!</> : <><Copy className="w-4 h-4 mr-2" /> Link kopieren</>}
          </Button>
        </div>

        {/* Link-Preview + Native-Share (Mobile) */}
        <div className="flex items-center gap-2 rounded-lg bg-black/50 border border-gray-800 px-3 py-2 text-xs text-gray-400 font-mono" data-testid="referral-link-preview">
          <Users className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
          <span className="truncate flex-1">{data.referral_url}</span>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              onClick={handleNativeShare}
              className="text-yellow-400 hover:text-yellow-300 font-bold text-[10px] uppercase tracking-wider ml-2"
              data-testid="referral-native-share"
            >
              Teilen
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ReferralShareCard;
